import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from 'react';

const DarkModeContext = createContext();

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (!context) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};

export const DarkModeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    // Check for saved preference or default to light mode
    const savedSettings = localStorage.getItem('appSettings');
    if (savedSettings) {
      try {
        const settings = JSON.parse(savedSettings);
        const shouldEnableDark = settings.darkMode === true;
        setDarkMode(shouldEnableDark);
        if (shouldEnableDark) {
          document.documentElement.classList.add('dark');
        } else {
          document.documentElement.classList.remove('dark');
        }
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.error('Failed to parse saved settings:', error);
        }
        // Default to light mode on error
        document.documentElement.classList.remove('dark');
      }
    } else {
      // No saved settings, default to light mode for better visibility
      document.documentElement.classList.remove('dark');
    }
  }, []);

  const toggleDarkMode = (enabled) => {
    setDarkMode(enabled);
    
    if (enabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    // Save to localStorage
    const currentSettings = JSON.parse(localStorage.getItem('appSettings') || '{}');
    const newSettings = { ...currentSettings, darkMode: enabled };
    localStorage.setItem('appSettings', JSON.stringify(newSettings));
  };

  return (
    <DarkModeContext.Provider value={{ darkMode, toggleDarkMode }}>
      {children}
    </DarkModeContext.Provider>
  );
};